import { ArrowDownOnSquareIcon } from '@heroicons/react/24/outline'
import imageCompression, { Options } from 'browser-image-compression'
import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { Button } from '~/elements'
import { Callout } from '~/elementsv2'

export type AvatarFormProps = {
  avatarUrl?: string
  setAvatarUrl: (url: string) => void
}
const MIME_TYPES = {
  JPEG_MIME_TYPE: 'image/jpeg',
  SVG_MIME_TYPE: 'image/svg+xml',
  WEBP_MIME_TYPE: 'image/webp',
  PNG_MIME_TYPE: 'image/png',
}
export function AvatarForm(props: AvatarFormProps) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setErrorMessage('')
    const imageFile = acceptedFiles[0]

    if (imageFile) {
      const maxFileSizeInBytes = 20 * 1024

      const fileType = imageFile.type
      const validImageTypes = Object.values(MIME_TYPES)

      if (!validImageTypes.includes(fileType)) {
        setErrorMessage(`Unsupported file type - ${fileType}. Please upload a JPEG, PNG, or SVG file.`)
        return
      }

      if (imageFile.size > 5 * 1024 * 1024) {
        setErrorMessage('File size exceeds 5MB. Please upload a smaller file.')
        return
      }

      if (imageFile.size < maxFileSizeInBytes) {
        const base64File = await imageCompression.getDataUrlFromFile(imageFile)
        props.setAvatarUrl(base64File)
        return
      }

      const options: Options = {
        maxSizeMB: maxFileSizeInBytes / 1024 / 1024,
        maxWidthOrHeight: 256,
        useWebWorker: true,
        fileType: fileType === MIME_TYPES.SVG_MIME_TYPE ? MIME_TYPES.SVG_MIME_TYPE : MIME_TYPES.WEBP_MIME_TYPE,
        initialQuality: 0.75,
      }

      try {
        const compressedFile = await imageCompression(imageFile, options)
        const base64File = await imageCompression.getDataUrlFromFile(compressedFile)
        props.setAvatarUrl(base64File)
      } catch (error) {
        setErrorMessage('Error compressing image. Please try again.')
      }
    }
  }, [])
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      void onDrop(acceptedFiles)
    },
  })

  return (
    <div {...getRootProps()} className="w-full">
      <>
        <input {...getInputProps()} />
        <div className="relative flex min-h-[120px] items-center rounded-lg border-2 border-dotted border-gray-4 p-5">
          {isDragActive && (
            <div className="bg-gray-1/80 absolute left-0 top-0 z-[2] flex h-full w-full flex-col items-center justify-center text-gray-11">
              <ArrowDownOnSquareIcon className="mb-4 h-8 w-8 animate-bounce opacity-50" />
            </div>
          )}
          <>
            <div className="z-1 relative h-[120px] w-[120px]">
              {props.avatarUrl && (
                <>
                  {/* Fake mask */}
                  <div className="z-1 absolute left-0 top-0 h-full w-full rounded-sm">
                    <img className="absolute h-full w-full object-cover" src={props.avatarUrl} alt="Bot Avatar" />
                    <div className="absolute h-full w-full bg-black/50"></div>
                  </div>
                  <img
                    className="relative aspect-square h-full w-full max-w-none rounded-full border border-gray-4 bg-gray-1 object-cover"
                    src={props.avatarUrl}
                    alt="Bot Avatar"
                  />
                </>
              )}
            </div>
            <div className="mx-6 h-[96px] w-[1px] bg-gray-4"></div>
            <div className="flex flex-grow flex-col items-center">
              <span className="text-sm text-gray-11">Drop image file here</span>
              <span className="mb-2 mt-1 text-sm text-gray-10">or</span>
              <Button variant="secondary" size="small">
                Select File
              </Button>
            </div>
          </>
        </div>
        {errorMessage && (
          <Callout className="mt-4 p-2" color="red">
            {errorMessage}
          </Callout>
        )}
      </>
    </div>
  )
}
